import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  User as FirebaseUser,
  OAuthProvider,
  signInWithRedirect,
  getRedirectResult,
} from 'firebase/auth';
import { auth } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { saveUserIdentityViaAPI } from '../../services/auth';
import { registerUser, UserRole, getUserProfile, checkWhitelistSignedUp } from '../../services/user.service';


import logo from '../../assets/logo.gif';

type CompleteOptions = { forceProfileCheck?: boolean };

export default function LoginForm() {
  const navigate = useNavigate();
  const { setUserData } = useAuth();
  const [isGoogleSigningIn, setIsGoogleSigningIn] = useState(false);
  const [isAppleSigningIn, setIsAppleSigningIn] = useState(false);
  const [isEmailSigningIn, setIsEmailSigningIn] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);

  // Email / password state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [emailStatus, setEmailStatus] = useState<{
    checking: boolean;
    hasSignedUp: boolean;
    inWhitelist: boolean;
    status: 'signed_up' | 'waitlisted' | 'not_found';
    message: string;
  }>({
    checking: false,
    hasSignedUp: false, //neutral start 
    inWhitelist: false,
    status: 'not_found', //start with password button hidden
    message: ''
  });

  /**
   * Common post-login flow. For Apple, optionally force a real profile existence check
   * to avoid backend false-negatives for needsProfile.
   */
  const completeSignIn = async (firebaseUser: FirebaseUser, options?: CompleteOptions) => {
 
    // 1. Store basic user info locally (for persistence)
    localStorage.setItem(
      'user',
      JSON.stringify({
        uid: firebaseUser.uid,
        email: firebaseUser.email,
       // displayName: firebaseUser.displayName || null,
       // photoURL: firebaseUser.photoURL || null,
      })
    );

    // 2. Exchange Firebase ID-token for backend JWT & session
    const authResponse = await saveUserIdentityViaAPI(firebaseUser);
    if (!authResponse) {
      throw new Error('Authentication failed');
    }


    // 3. Register / fetch role & profile
  
    const registerData = await registerUser(firebaseUser.email || '');
    const actualData: any = registerData.data?.data || registerData.data; // supports both shapes
    const userRole: UserRole | null = actualData?.role || null;

    const hasCompleteProfile = (p: any) => !!(p && p.fullName && p.phoneNumber && p.studioName);

    // Start with server-provided value
    let needsProfile: boolean = !!actualData?.needsProfile;

    // If server says profile is needed but response also carries complete fields, trust the fields
    const userObj: any = actualData?.user;
    const hasUserProfileFields = userObj && userObj.fullName && userObj.phoneNumber && userObj.studioName;
    if (needsProfile && hasUserProfileFields) {
      needsProfile = false;
    }

    // Resolve profile data and final decision
    let profileData: any = null;
    if (options?.forceProfileCheck) {
      try {
        profileData = await getUserProfile();
      } catch {
        profileData = null;
      }
      needsProfile = !hasCompleteProfile(profileData);
    } else if (!needsProfile) {
      // Fetch profile only if server indicates it exists
      try {
        profileData = await getUserProfile();
      } catch (err) {
        console.error('Could not fetch profile after login:', err);
      }
    }

    // Persist final hasProfile decision
    localStorage.setItem('hasProfile', (!needsProfile).toString());

    // 4. Update global auth context
    setUserData(firebaseUser, userRole, needsProfile ? null : profileData);

    // 5. Redirect based on profile completion
    navigate(needsProfile ? '/update-profile' : '/dashboard');
  };

  // ********************
  // Google sign-in
  // ********************
  const handleGoogleSignIn = async () => {
    setIsGoogleSigningIn(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, provider);
      await completeSignIn(result.user);
    } catch (error) {
      console.error('Google sign-in failed:', error);
      alert('Google sign-in failed. Please try again.');
    } finally {
      setIsGoogleSigningIn(false);
    }
  };

  // ********************
  // Apple sign-in (popup with redirect fallback)
  // ********************
  const handleAppleSignIn = async () => {
    setIsAppleSigningIn(true);
    let keepLoadingThroughRedirect = false;
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      const result = await signInWithPopup(auth, provider);
      await completeSignIn(result.user);
    } catch (error: any) {
      const popupBlocked = error?.code === 'auth/popup-blocked' || error?.code === 'auth/popup-closed-by-user';
      const notSupported = error?.code === 'auth/operation-not-supported-in-this-environment';
      if (popupBlocked || notSupported) {
        try {
          sessionStorage.setItem('appleRedirectPending', '1');
          const provider = new OAuthProvider('apple.com');
          provider.addScope('email');
          provider.addScope('name');
          // Keep loading state until redirect completes to avoid flicker
          keepLoadingThroughRedirect = true;
          await signInWithRedirect(auth, provider);
          return;
        } catch (redirectErr) {
          console.error('Apple redirect sign-in failed:', redirectErr);
          alert('Apple sign-in failed. Please try again.');
        }
      } else {
        console.error('Apple sign-in failed:', error);
        alert('Apple sign-in failed. Please try again.');
      }
    } finally {
      if (!keepLoadingThroughRedirect) {
        setIsAppleSigningIn(false);
      }
    }
  };

  // Handle redirect result (mobile Safari / environments without popup)
  useEffect(() => {
    let mounted = true;
    const pending = sessionStorage.getItem('appleRedirectPending') === '1';
    if (pending) setIsAppleSigningIn(true);
    (async () => {
      try {
        const redirectResult = await getRedirectResult(auth);
        if (redirectResult && redirectResult.user) {
          sessionStorage.removeItem('appleRedirectPending');
          if (mounted) {
            await completeSignIn(redirectResult.user);
          }
        }
      } catch (err) {
        console.error('Error handling Apple redirect result:', err);
        sessionStorage.removeItem('appleRedirectPending');
      } finally {
        if (mounted) setIsAppleSigningIn(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Check email status when user finishes typing (debounced)
  useEffect(() => {
    // Reset status if email is empty or form is hidden
    if (!email || !showEmailForm) {
      setEmailStatus({ 
        checking: false, 
        hasSignedUp: false,
        inWhitelist: false,
        status: 'not_found',
        message: '' 
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailStatus({ 
        checking: false, 
        hasSignedUp: false,
        inWhitelist: false,
        status: 'not_found',
        message: '' 
      });
      return;
    }

    // Debounce: wait 800ms after user stops typing
    const timeoutId = setTimeout(async () => {
      setEmailStatus({ 
        checking: true, 
        hasSignedUp: false,
        inWhitelist: false,
        status: 'not_found',
        message: '' 
      });
      
      try {
        const result = await checkWhitelistSignedUp(email);
        
        // Determine message based on status
        let message = '';
        if (result.status === 'waitlisted') {
          message = "You're on our waitlist! Click below to complete your account setup.";
        } else if (result.status === 'not_found') {
          message = "Thank you for your interest in CR3ATE! Please join our waitlist and someone from the team will reach out to you.";
        }
        
        setEmailStatus({
          checking: false,
          hasSignedUp: result.hasSignedUp,
          inWhitelist: result.inWhitelist,
          status: result.status,
          message
        });
      } catch (error) {
        console.error('Error checking email status:', error);
        // On error, assume they have account (safer for existing users)
        setEmailStatus({ 
          checking: false, 
          hasSignedUp: false,
          inWhitelist: false,
          status: 'not_found',
          message: '' 
        });
      }
    }, 800); // Wait 800ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [email, showEmailForm]);


  // ********************
  // Email / password sign-in
  // ********************
  const handleEmailPasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return alert('Please enter both email and password.');
    
    // If user isn't signed up, redirect appropriately
    if (emailStatus.status === 'waitlisted') {
      navigate(`/signup?email=${encodeURIComponent(email)}&from=waitlist`);
      return;
    }
    
    if (emailStatus.status === 'not_found') {
      // They tried to sign in but aren't in the system
      navigate(`/request-waitlist?email=${encodeURIComponent(email)}&from=login`);
      return;
    }
    
    setIsEmailSigningIn(true);
    
    try {
      const res = await signInWithEmailAndPassword(auth, email, password);
      await completeSignIn(res.user);
    } catch (error: any) {
      console.error('Email/password sign-in failed:', error);
      
      let msg = 'Sign-in failed. Please try again.';
      if (error?.code === 'auth/wrong-password') msg = 'Incorrect password.';
      if (error?.code === 'auth/user-not-found') msg = 'No account found with that email.';
      if (error?.code === 'auth/invalid-email') msg = 'Invalid email address.';
      if (error?.code === 'auth/too-many-requests') msg = 'Too many failed attempts. Please try again later.';
      
      alert(msg);
    } finally {
      setIsEmailSigningIn(false);
    }
  };

  //Direct redirect handler for waitlist users
  const handleWaitlistRedirect = () => {
    navigate(`/signup?email=${encodeURIComponent(email)}&from=waitlist`);
  };

   //Direct redirect handler for new users
  const handleNewUserRedirect = () => {
    navigate(`/request-waitlist?email=${encodeURIComponent(email)}&from=login`);
  };

  // ********************
  // Forgot password
  // ********************
  const handleForgotPassword = async () => {
    if (!email) return alert('Enter your email first.');
    try {
      await sendPasswordResetEmail(auth, email);
      alert('Password reset email sent! Check your inbox.');
    } catch (err) {
      console.error('Error sending reset email:', err);
      alert('Could not send reset email. Please verify the address and try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#161513] flex flex-col items-center justify-center p-6">
      {/* Header / logo */}
      <div className="text-center mb-8">
        <img src={logo} alt="cr3ate.ai" className="h-48 mx-auto mb-4" />
        <h1 className="font-light text-xl text-[#0CB4E4] font-brand">
          CR3ATE.AI
        </h1>
        <p className="text-sm text-gray-300 mt-1">Recr3ate your reality in 3D</p>
      </div>

      {/* Primary CTA: Google */}
      <button
        onClick={handleGoogleSignIn}
        disabled={isGoogleSigningIn}
        className="w-full max-w-md py-4 px-6 bg-white text-[#161513] font-semibold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.99] transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none border border-white/60"
        aria-label="Continue with Google"
      >
        {isGoogleSigningIn ? (
          <svg
            className="animate-spin -ml-1 mr-2 h-5 w-5 text-current"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        ) : (
          <svg className="w-5 h-5" viewBox="0 0 533.5 544.3" xmlns="http://www.w3.org/2000/svg">
            <path d="M533.5 278.4c0-17.4-1.6-34.1-4.6-50.4H272v95.3h147.4c-6.4 34.4-25.6 63.5-54.7 83v68h88.4c51.8-47.7 80.4-118 80.4-196z" fill="#4285F4"/>
            <path d="M272 544.3c73.6 0 135.5-24.4 180.6-66.3l-88.4-68c-24.6 16.5-56.2 26.3-92.2 26.3-70.9 0-131-47.9-152.4-112.3H31.4v70.9C76.9 483 168.8 544.3 272 544.3z" fill="#34A853"/>
            <path d="M119.6 323.9c-10-29.4-10-61 0-90.4V162.6H31.4c-29.4 58.9-29.4 128.8 0 187.7l88.2-26.4z" fill="#FBBC05"/>
            <path d="M272 107.7c39 0 74.1 13.4 101.7 39.8l76.4-76.4C407.3 24.4 345.4 0 272 0 168.8 0 76.9 61.4 31.4 162.6l88.2 70.9C141 155.6 201.1 107.7 272 107.7z" fill="#EA4335"/>
          </svg>
        )}
        <span>{isGoogleSigningIn ? 'Signing in…' : 'Continue with Google'}</span>
      </button>

      {/* Apple */}
      <button
        onClick={handleAppleSignIn}
        disabled={isAppleSigningIn}
        className="w-full max-w-md mt-3 py-4 px-6 bg-white text-[#161513] font-semibold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.99] transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed border border-white/10"
        aria-label="Continue with Apple"
      >
        {isAppleSigningIn ? (
          <svg
            className="animate-spin -ml-1 mr-2 h-5 w-5 text-current"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        ) : (
          <svg className="w-5 h-5" viewBox="0 0 384 512" fill="currentColor">
            <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
          </svg>
        )}
        <span>{isAppleSigningIn ? 'Signing in…' : 'Continue with Apple'}</span>
      </button>

      {/* Secondary links under Google */}
      {!showEmailForm && (
        <p className="mt-4 text-sm text-gray-300 text-center">
          Don’t have a Cr3ate account?{' '}
          <button
            type="button"
            onClick={() => navigate('/signup')}
            className="text-[#0CB4E4] underline underline-offset-2 hover:opacity-90"
          >
            Sign up
          </button>{' '}
          or{' '}
          <button
            type="button"
            onClick={() => setShowEmailForm(true)}
            className="text-[#0CB4E4] underline underline-offset-2 hover:opacity-90"
          >
            Sign in
          </button>{' '}
            with email.
        </p>
      )}

      {/* Email / Password form */}
      {showEmailForm && (
        <form
          onSubmit={handleEmailPasswordSignIn}
          className="w-full max-w-md bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 space-y-4 mt-6"
        >
          {/* Email field with status indicator */}
          <div>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-white/90 text-[#161513] placeholder-gray-500 focus:outline-none"
            />
            
            {/* NEW: Email status messages */}
            {emailStatus.checking && (
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-300">
                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Checking account status...
              </div>
            )}
            
            {emailStatus.message && !emailStatus.checking && (
              <div className="mt-2 bg-[#0CB4E4]/20 border border-[#0CB4E4]/40 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-[#0CB4E4] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-xs text-gray-200">{emailStatus.message}</p>
                    
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Password field - only show for signed up users */}
          {emailStatus.status === 'signed_up' && (
            <>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-white/90 text-[#161513] placeholder-gray-500 focus:outline-none"
              />
              <button
                type="submit"
                disabled={isEmailSigningIn || emailStatus.checking}
                className="w-full py-3 bg-[#0C5484] text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isEmailSigningIn ? 'Signing in…' : 'Sign in'}
              </button>
            </>
          )}

          {/* User is on waitlist - show setup button */}
          {emailStatus.status === 'waitlisted' &&  emailStatus.message && (
            <button
              type="button"
              onClick={handleWaitlistRedirect}
              className="w-full py-3 bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/80 text-[#161513] font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              Complete Account Setup →
            </button>
          )}

          {/* User not in waitlist - show join waitlist button */}
          {emailStatus.status === 'not_found' && emailStatus.message && (
            <button
              type="button"
              onClick={handleNewUserRedirect}
              className="w-full py-3 bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/80 text-[#161513] font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              Join Our Waitlist →
            </button>
          )}

          <div className="flex justify-between mt-2">
            {emailStatus.status === 'signed_up' && (
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-xs text-[#0CB4E4] hover:underline pl-2"
              >
                Reset Password
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setShowEmailForm(false);
                setEmail('');
                setPassword('');
                setEmailStatus({ 
                  checking: false, 
                  hasSignedUp: false,
                  inWhitelist: false,
                  status: 'not_found',
                  message: '' 
                });
              }}
              className="text-xs text-gray-400 hover:text-white pr-2 ml-auto"
            >
              ← Back
            </button>
          </div>
        </form>
      )}
    </div>
  );
}